/**
 * /admin/guide — the Admin Handbook.
 *
 * A living reference for new (and forgetful) admins: what the site does,
 * where everything lives, what runs automatically and when, how-to guides
 * and an FAQ. KEEP THIS UPDATED whenever a feature is added or changed.
 */
import React, { useState } from 'react';
import { BookOpen, Bot, HelpCircle, ListChecks, Map, Medal, Search, Users } from 'lucide-react';

const SECTIONS = [
  { id: 'overview', label: 'What this site is', icon: Map },
  { id: 'public-pages', label: 'The public site', icon: Map },
  { id: 'admin-areas', label: 'Admin areas explained', icon: ListChecks },
  { id: 'automation', label: 'Automated actions', icon: Bot },
  { id: 'membership', label: 'Membership levels (briefing card)', icon: Medal },
  { id: 'partners', label: 'Trade partners lifecycle', icon: Users },
  { id: 'howto', label: 'How-to guides', icon: ListChecks },
  { id: 'faq', label: 'FAQ', icon: HelpCircle },
];

const AUTOMATIONS = [
  ['Event scraper', 'Every Monday 06:00', 'Pulls events from all enabled feed sources (ICS, JSON-LD, Ticketsolve, Loughborough Town Hall, Leicestershire County Council via proxy, Eventbrite). Everything lands in Events → Pending for your approval — nothing goes live automatically.'],
  ['StayPlayExplore / Perfect Break sync', 'Every Monday 05:30', 'Checks the official partner sitemaps and updates SPE/PB badges on listings; suggests Leicestershire venues on their site that are missing from ours (Attractions page panel).'],
  ['Listing freshness cycle', 'Daily 07:45', 'Two jobs: (1) any active trade partner with a linked listing gets a "please check your listing" email every 90 days; (2) any published listing untouched for 6 months is flagged to admins via a notification and the Listing freshness panel on the Attractions page.'],
  ['Freshness digest', '1st of month 08:15', 'Posts a notification summarising who received a review email in the last 30 days, who updated their listing, and who stayed silent — so you can chase them. Also viewable any time via the Monthly digest button in the Listing freshness panel.'],
  ['Friday newsletter', 'Every Friday 07:00', 'AI writes the weekend digest from approved events + weather, builds a draft with trending attraction, awards leader and member spotlight, then sends via EmailOctopus. History in Admin → Newsletter.'],
  ['Award cycle status flip', 'Daily 02:00', 'Auto-moves award cycles between nominations open/closed and voting open/closed based on the dates you set on the cycle.'],
  ['Link health check', 'Daily 03:15', 'Checks every outbound URL on events and attractions; broken ones appear on the Overview dashboard with a Fix link.'],
  ['Past-event archiver', 'Daily 04:00', 'Moves expired events to Archived so the What\'s On page stays current.'],
  ['Trash auto-purge', 'Daily 04:15', 'Permanently deletes soft-deleted venues after 90 days in the Trash.'],
  ['Event image backfill', 'Daily 04:30', 'Fills missing event images: event page og:image → venue logo → branded card.'],
  ['Venue listings rescrape', 'Sunday 22:00', 'Re-checks venue What\'s-On pages for newly announced events.'],
  ['Local news + partner resource scrape', 'Sunday 06:00 / 06:30', 'Collects local tourism news and trade resources into their review queues.'],
  ['AI town inspirations', 'Daily 18:15', 'Regenerates the "why visit today" paragraph per town; drafts need approval in AI Inspirations.'],
  ['Reassurance filter (Sentinel)', 'Daily 21:00', 'Scans new copy for content-policy problems and flags anything suspicious.'],
  ['Quarterly prize-draw reminder', '1st of Jan/Apr/Jul/Oct 08:00', 'Reminds you to draw and contact the newsletter competition winner.'],
];

const TIERS = [
  {
    name: 'Gold', color: 'bg-mustard/20 border-mustard/50',
    price: 'Top tier', bullets: [
      'Unlimited description length — tell the full story',
      '10 photos in total (main photo + 9 gallery images)',
      'Website, phone, email, prices, opening hours all shown',
      'Highest placement weighting in listings and search',
      'Featured member rotation on the homepage',
    ],
  },
  {
    name: 'Silver', color: 'bg-charcoal/5 border-charcoal/20',
    price: 'Mid tier', bullets: [
      'Description up to 320 words',
      '5 photos in total (main photo + 4 gallery images)',
      'Website, phone, email, prices, opening hours all shown',
      'Priority placement above Bronze and non-members',
    ],
  },
  {
    name: 'Bronze', color: 'bg-heritage/5 border-heritage/20',
    price: 'Entry tier', bullets: [
      'Description up to 60 words',
      '2 photos in total (main photo + 1 gallery image)',
      'Website and phone number shown',
      'Placement above non-members',
    ],
  },
  {
    name: 'Non-member', color: 'bg-white border-charcoal/10',
    price: 'Free', bullets: [
      'Name, town and a 20-word summary only',
      '1 photo (main photo only)',
      'No phone number displayed',
      'Listed after all paying members',
    ],
  },
];

const HOWTOS = [
  {
    q: 'Create a new venue listing (tier first)',
    a: 'Attractions → New. You are first asked which membership level the venue holds (None / Bronze / Silver / Gold) — this sets the description and photo limits for the listing and its placement on the public site. Pick the level, fill in the details (the tier limits are shown in the editor), add a postcode (drives the map pin) and tick Published when ready.',
  },
  {
    q: 'Approve a trade partner application',
    a: 'Partner Applications → pending rows are always at the top. Click Approve, choose their membership tier if they\'ve paid, and a single-use set-password link is emailed to them automatically (also shown to you as a copy-paste fallback). Once they set a password their account is active.',
  },
  {
    q: 'Review a partner\'s listing edits',
    a: 'Listing edits (sidebar) → each pending edit shows a field-by-field diff against the live listing. Approve applies it instantly and emails the partner; Reject lets you add a note explaining why. Partners can never change the live site directly.',
  },
  {
    q: 'Approve partner-submitted events',
    a: 'Trade partners can submit events from their My Listing page. These arrive in Events with status pending (always sorted to the top) and a notification fires. Review, tidy the copy if needed, and Approve.',
  },
  {
    q: 'Run the event scraper by hand',
    a: 'Event Scraper → "Run scraper now". Results land in Events → Pending. Use "Try this venue" to dry-run a single URL before adding it as a feed. Add/disable feeds in the same page.',
  },
  {
    q: 'Check listing freshness / stale listings',
    a: 'Attractions → Listing freshness panel. It lists every published listing untouched for 6+ months (these also trigger a notification automatically). Click a row to open and refresh it — saving clears the flag. "Run freshness cycle now" also sends any due quarterly partner reminder emails immediately. The "Monthly digest" button shows who was emailed vs who actually updated, with a Chase link for silent members.',
  },
  {
    q: 'How partners upgrade their membership (paid, online)',
    a: 'Partners see an "Upgrade for more photos & words" button on their My Listing page, and an "Upgrade now — pay online" button whenever an edit exceeds their tier limits. Both open a Stripe checkout showing only tiers above their current one (Bronze £250 / Silver £500 / Gold £1,000 annual — edit prices in Payments). On payment, their account tier, listing tier and renewal date update automatically and you get a payment notification.',
  },
  {
    q: 'Run a Tourism Awards cycle',
    a: 'Awards → create a cycle, seed the 7 default categories (editable), set the nomination/voting dates, and the status flips automatically each night. Public nominations need your approval before they hit the ballot. Publish winners at the end — trophy badges apply to listings automatically. You can also bulk-import historic winners from a press release, spreadsheet or PDF via the document scanner.',
  },
  {
    q: 'Fix a broken image or missing photo',
    a: 'Attractions → Photo quality panel → "Check all photos now" scans every venue photo and flags anything broken, tiny or oddly shaped — click a flagged venue to open it and swap the image. Also: Overview → "Needs photo" tile for venues with no image at all. The Media Library stores everything permanently.',
  },
  {
    q: 'Add another admin',
    a: 'Admin team (owner only) → Invite. They get a 7-day one-time link by email; if email fails you can copy the link manually. Editors see everything except Running costs, Admin team and Audit log.',
  },
  {
    q: 'Send the newsletter manually',
    a: 'Newsletter → "Run Friday pipeline now", or compose from a digest in Weekly Digest → newsletter draft → send. EmailOctopus handles delivery to subscribers.',
  },
];

const FAQ = [
  ['Why is an event/venue I can see in admin not on the public site?', 'Check its status: events must be approved, attractions must have Published ticked. Non-member venues also show only a minimal card (name, town, 20-word summary) by design.'],
  ['A partner says they can\'t log in.', 'Partner Applications → find their row. If status is "approved" they never set their password — re-approve to send a fresh link. If "pending", approve them. Password reset links come from the Forgot Password link on /partners.'],
  ['Why did a partner\'s edit get blocked with an upgrade message?', 'Tier limits are enforced on submission: e.g. a Bronze partner submitting a 300-word description is asked to upgrade. This is intentional — see the Membership levels section.'],
  ['The scraper found the wrong image / wrong town for an event.', 'Open the event in Events, correct it, and approve. For persistent offenders, tell the developer — venue-specific parsers can be added (Loughborough Town Hall has one for exactly this reason).'],
  ['Why does Leicestershire County Council scraping work but slowly?', 'Their website blocks our server\'s network entirely, so we fetch it through a reader proxy. It runs with the normal Monday scrape, just a little slower.'],
  ['What emails go out automatically?', 'Partner approval + password links, edit approved/rejected notices, quarterly listing-check reminders, "you\'ve been nominated" award emails (from the queue you approve), Friday newsletter, and admin alert emails to the notification recipients list.'],
  ['Where do I see what everything costs?', 'Running costs (owner only) — live AI spend, email quota, fixed fees, and a daily AI spend cap you can set.'],
  ['Something broke — where do I look first?', 'Overview dashboard: broken links panel, pending counts, health tiles. Then Notifications. The "Heal everything I can" button fixes common data issues (geocoding, opening days, stale text) in one click.'],
  ['How do I take the site content to the printed guide or socials?', 'Every listing page is public and linkable. Use Usage Stats to see which venues and events get the most attention before choosing what to feature.'],
];

function SectionTitle({ id, icon: Icon, children }) {
  return (
    <h2 id={id} className="font-display text-2xl flex items-center gap-2 mb-4 pt-6 scroll-mt-24">
      <Icon size={20} className="text-heritage" /> {children}
    </h2>
  );
}

export default function AdminGuide() {
  const [q, setQ] = useState('');
  const norm = q.trim().toLowerCase();
  const match = (txt) => !norm || txt.toLowerCase().includes(norm);

  return (
    <div data-testid="admin-guide-page">
      <div className="label text-heritage mb-2">Handbook</div>
      <h1 className="font-display text-4xl mb-2 flex items-center gap-3"><BookOpen size={30}/> Admin Guide</h1>
      <p className="text-sm text-muted mb-6 max-w-2xl">
        Everything a new admin needs to run GoLeicestershire — what the site does, what happens
        automatically, and step-by-step how-tos. This page is kept up to date as features change.
      </p>

      <div className="relative max-w-md mb-8">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40"/>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the guide (e.g. scraper, tier, newsletter)…"
          data-testid="guide-search" className="w-full bg-white border border-charcoal/15 rounded-full pl-9 pr-4 py-2 text-sm focus:outline-none focus:border-heritage"/>
      </div>

      <div className="flex flex-wrap gap-2 mb-10">
        {SECTIONS.map((s) => (
          <a key={s.id} href={`#${s.id}`} className="px-4 py-1.5 rounded-full text-xs font-semibold bg-white border border-charcoal/15 hover:border-heritage" data-testid={`guide-toc-${s.id}`}>
            {s.label}
          </a>
        ))}
      </div>

      <div className="max-w-4xl space-y-2">
        <SectionTitle id="overview" icon={Map}>What this site is</SectionTitle>
        <div className="bg-white border border-charcoal/10 rounded-2xl p-6 text-sm leading-relaxed space-y-3">
          <p><strong>GoLeicestershire.com</strong> is the county&apos;s destination platform: a public visitor site (attractions, events, towns, itineraries, awards) powered by this admin dashboard. Revenue comes from tiered venue memberships (Bronze/Silver/Gold), StayPlayExplore packages and the annual Tourism Awards.</p>
          <p>Three golden rules:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Nothing user-submitted goes live without approval</strong> — partner edits, partner events, award nominations and scraped events all land in pending queues (always sorted to the top of their lists).</li>
            <li><strong>Membership tier controls what a listing shows</strong> — the limits are enforced automatically on the public site and on partner submissions.</li>
            <li><strong>The robots do the heavy lifting</strong> — scrapers, newsletters, freshness reminders and health checks run on schedules (see Automated actions below). Your job is reviewing their output, not creating from scratch.</li>
          </ul>
        </div>

        <SectionTitle id="public-pages" icon={Map}>The public site</SectionTitle>
        <div className="bg-white border border-charcoal/10 rounded-2xl p-6 text-sm leading-relaxed">
          <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
            <li><strong>/</strong> — weather-aware homepage, daily rotating members row</li>
            <li><strong>/explore</strong> — all attractions with filters + map</li>
            <li><strong>/accommodation</strong> — places to stay</li>
            <li><strong>/events</strong> — What&apos;s On (approved events only)</li>
            <li><strong>/towns</strong> — city + market town pages with AI narratives</li>
            <li><strong>/plan</strong> — Plan My Day itinerary builder</li>
            <li><strong>/half-days</strong> — hand-curated loops</li>
            <li><strong>/packages</strong> — StayPlayExplore packages</li>
            <li><strong>/awards</strong> — Tourism Awards (nominations/voting/winners)</li>
            <li><strong>/partners</strong> — trade partner portal (login required)</li>
            <li><strong>/partners/my-listing</strong> — partner self-service editor + event submission</li>
            <li><strong>/digest</strong> — the weekly AI digest</li>
          </ul>
        </div>

        <SectionTitle id="admin-areas" icon={ListChecks}>Admin areas explained</SectionTitle>
        <div className="bg-white border border-charcoal/10 rounded-2xl p-6 text-sm">
          <table className="w-full text-left">
            <tbody className="divide-y divide-charcoal/5">
              {[
                ['Overview', 'Health tiles (pending counts, needs-photo, broken links), "Heal everything I can" maintenance button.'],
                ['Lester Chatbot', 'The public site\u2019s friendly chat assistant. Review every visitor conversation and add teaching notes (ground-truth facts/corrections) to improve his answers.'],
                ['Notifications', 'In-app alert inbox + who receives email alerts.'],
                ['Reassurance filter', 'Content Sentinel — flags risky copy before it embarrasses you.'],
                ['Usage Stats', 'GDPR-safe analytics: traffic, top venues, conversions, searches.'],
                ['SEO status', 'Sitemap, meta and structured-data health.'],
                ['Attractions', 'The heart of the site — every venue. Includes the StayPlayExplore sync panel and the Listing freshness panel. Pending drafts always sort to the top.'],
                ['Bulk Import / Media Library', 'CSV upsert of venues; all uploaded images with usage tracking.'],
                ['Events', 'Approve/reject queue (pending first), trash, image backfill.'],
                ['Event Scraper', 'Feed sources, run history, dry-run tester, near-duplicate scanner.'],
                ['Awards', 'Cycles, categories, nominee moderation, email queue, historic import scanner.'],
                ['AI Inspirations', 'Approve the nightly town narratives.'],
                ['Members', 'Featured member management.'],
                ['Partner Applications', 'Approve/reject trade partner access (pending first). Sets membership tier.'],
                ['Trade Pages / Resource Queue', 'Content pages and scraped resources for the partner portal.'],
                ['Payments', 'Stripe payment records.'],
                ['Launch Campaign', 'The venue-claim email campaign + AI starter drafts.'],
                ['Listing edits', 'Partner-submitted changes with field-by-field diffs.'],
                ['Packages / County Locations', 'StayPlayExplore products; weather locations.'],
                ['Weekly Digest / Newsletter / Subscribers', 'AI digest, Friday pipeline, EmailOctopus list.'],
                ['Running costs (owner)', 'AI + email spend, budget cap.'],
                ['Admin team (owner)', 'Invite/remove admins.'],
                ['Audit log (owner)', 'Who changed what, when.'],
              ].filter(([a, b]) => match(a + ' ' + b)).map(([area, desc]) => (
                <tr key={area}>
                  <td className="py-2 pr-4 font-semibold whitespace-nowrap align-top">{area}</td>
                  <td className="py-2 text-charcoal/75">{desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <SectionTitle id="automation" icon={Bot}>Automated actions — what runs when</SectionTitle>
        <div className="bg-white border border-charcoal/10 rounded-2xl p-6 text-sm" data-testid="guide-automation-table">
          <p className="text-xs text-muted mb-4">All times Europe/London. Every automated action is review-first: robots gather, you approve.</p>
          <table className="w-full text-left">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-charcoal/50">
                <th className="pb-2 pr-4">Job</th><th className="pb-2 pr-4 whitespace-nowrap">Schedule</th><th className="pb-2">What it does</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-charcoal/5 align-top">
              {AUTOMATIONS.filter((r) => match(r.join(' '))).map(([job, when, what]) => (
                <tr key={job}>
                  <td className="py-2.5 pr-4 font-semibold whitespace-nowrap">{job}</td>
                  <td className="py-2.5 pr-4 text-heritage whitespace-nowrap">{when}</td>
                  <td className="py-2.5 text-charcoal/75">{what}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <SectionTitle id="membership" icon={Medal}>Membership levels — briefing card</SectionTitle>
        <div className="bg-white border border-charcoal/10 rounded-2xl p-6 text-sm" data-testid="guide-membership-card">
          <p className="mb-4 text-charcoal/75">
            Use this as your script when explaining the levels to venues. The same limits are enforced
            automatically on the public site and when partners submit edits — if they exceed their level,
            they see a polite upgrade prompt rather than an error.
          </p>
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            {TIERS.map((t) => (
              <div key={t.name} className={`border rounded-2xl p-5 ${t.color}`} data-testid={`guide-tier-${t.name.toLowerCase().replace(/[^a-z]/g, '')}`}>
                <div className="flex items-baseline justify-between mb-2">
                  <span className="font-display text-xl">{t.name}</span>
                  <span className="text-xs text-charcoal/50 font-semibold uppercase tracking-wider">{t.price}</span>
                </div>
                <ul className="space-y-1.5 text-charcoal/80">
                  {t.bullets.map((b) => <li key={b} className="flex gap-2"><span className="text-heritage">·</span>{b}</li>)}
                </ul>
              </div>
            ))}
          </div>
          <div className="bg-cream/60 border border-charcoal/10 rounded-xl p-4 text-xs text-charcoal/70">
            <strong>The one-line pitch per level:</strong> Non-member = &ldquo;you exist&rdquo;. Bronze = &ldquo;you can be found and contacted&rdquo;.
            Silver = &ldquo;you can tell your story properly with a small gallery&rdquo;. Gold = &ldquo;the full showcase, best placement, homepage
            exposure&rdquo;. All members can log in, edit their listing (admin-approved), and submit events to What&apos;s On.
            A printable copy lives in the project files as <code>MEMBER_TIER_BRIEFING.md</code>.
          </div>
        </div>

        <SectionTitle id="partners" icon={Users}>Trade partners lifecycle</SectionTitle>
        <div className="bg-white border border-charcoal/10 rounded-2xl p-6 text-sm leading-relaxed">
          <ol className="list-decimal pl-5 space-y-2 text-charcoal/80">
            <li><strong>Apply</strong> — venue applies at /partners (or claims via a launch-campaign link). Lands in Partner Applications as pending.</li>
            <li><strong>Approve</strong> — you approve (optionally setting a paid tier); they get a one-time password link by email.</li>
            <li><strong>Self-service</strong> — they log in to edit their listing, submit events, and request extra listings. Everything queues for your approval.</li>
            <li><strong>Quarterly nudge</strong> — every 90 days they automatically get a &ldquo;check your listing&rdquo; email (fully automatic, no action needed from you).</li>
            <li><strong>Stale flag</strong> — if a listing goes 6 months with no edits by anyone (them or you), it&apos;s flagged in the Attractions freshness panel and you get a notification.</li>
          </ol>
        </div>

        <SectionTitle id="howto" icon={ListChecks}>How-to guides</SectionTitle>
        <div className="space-y-3">
          {HOWTOS.filter((h) => match(h.q + ' ' + h.a)).map((h) => (
            <details key={h.q} className="bg-white border border-charcoal/10 rounded-2xl p-5 group" data-testid="guide-howto-item">
              <summary className="font-semibold text-sm cursor-pointer list-none flex items-center justify-between">
                {h.q}<span className="text-charcoal/30 group-open:rotate-90 transition-transform">›</span>
              </summary>
              <p className="text-sm text-charcoal/75 mt-3 leading-relaxed">{h.a}</p>
            </details>
          ))}
        </div>

        <SectionTitle id="faq" icon={HelpCircle}>FAQ</SectionTitle>
        <div className="space-y-3 pb-16">
          {FAQ.filter(([a, b]) => match(a + ' ' + b)).map(([question, answer]) => (
            <details key={question} className="bg-white border border-charcoal/10 rounded-2xl p-5 group" data-testid="guide-faq-item">
              <summary className="font-semibold text-sm cursor-pointer list-none flex items-center justify-between">
                {question}<span className="text-charcoal/30 group-open:rotate-90 transition-transform">›</span>
              </summary>
              <p className="text-sm text-charcoal/75 mt-3 leading-relaxed">{answer}</p>
            </details>
          ))}
        </div>
      </div>
    </div>
  );
}
